import { useCallback, useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Link, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { useSetAtom } from 'jotai';
import { Action, ItemType, Page } from '@/../../server/types/loggingTypes';
import chevronLeft from '@/assets/chevron_left.svg';
import logo from '@/assets/logo.png';
import Button from '@/components/lib/Button';
import PhoneInput from '@/components/lib/PhoneInput';
import useRouterNavigation from '@/hooks/useRouterNavigation';
import { signupPhoneAtom } from '@/store/signupAtoms';
import { getStatsigClient } from '@/utils/statsig';

export const Route = createFileRoute('/signup/phone-signup')({
  component: PhoneSignupPage,
});

function PhoneSignupPage() {
  const toast = useToast();
  const navigate = useNavigate({ from: '/signup/phone-signup' });
  const { back } = useRouterNavigation();

  const [phoneNumber, setPhoneNumber] = useState('');

  const setSignupPhone = useSetAtom(signupPhoneAtom);

  const handleSwitchToEmail = useCallback(() => {
    navigate({ to: '/signup/email-signup' });
  }, [navigate]);

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      if (!phoneNumber) {
        getStatsigClient().logEvent(Page.PHONE_NUMBER_PAGE, Action.TYPING, {
          item_name: 'phone',
        });
      }
      setPhoneNumber(value);
    },
    [phoneNumber],
  );

  const handleSendOtp = useCallback(async () => {
    if (!phoneNumber || !/^\+[1-9]\d{3,14}$/.test(phoneNumber.replace(/\s+/g, ''))) {
      toast({ title: 'Please enter a valid phone number.', status: 'warning', duration: 3000 });
      return;
    }

    try {
      getStatsigClient().logEvent(Page.PHONE_NUMBER_PAGE, Action.CLICK, {
        item_name: 'send_phone_otp',
        item_type: ItemType.BUTTON,
      });

      void axios.post('/api/auth/sendSmsVerificationCode', { phoneNumber });
      setSignupPhone(phoneNumber);
      navigate({
        to: '/signup/phone-verification',
      });
    } catch (error) {
      console.error(error);
      toast({ title: 'Something went wrong. Please try again.', status: 'error', duration: 3000 });
    }
  }, [phoneNumber, navigate, toast, setSignupPhone]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSendOtp();
      }
    },
    [handleSendOtp],
  );

  useEffect(() => {
    // Log view event for phone number page
    getStatsigClient().logEvent(Page.PHONE_NUMBER_PAGE, Action.VIEW);
  }, []);

  return (
    <div className="flex flex-col items-center min-h-screen bg-[#121212]">
      {/* Header */}
      <div className="relative flex flex-row items-center justify-center py-3 px-4 w-full">
        <img src={chevronLeft} alt="Back" className="absolute left-4 size-6 cursor-pointer" onClick={back} />
        <img src={logo} alt="Bond Logo" className="h-8 object-contain" />
      </div>

      <h1 className="text-white text-[34px]/[40px] tracking-tight text-center max-w-[280px] font-bold mb-6 mt-6">
        What's your phone number?
      </h1>

      <div className="w-full px-6 flex flex-col items-center">
        <div className="w-full max-w-[334px]">
          <PhoneInput onChange={handlePhoneNumberChange} onKeyDown={handleKeyDown} autoFocus />

          <div className="mt-8">
            <Link color="#ff0050" onClick={handleSwitchToEmail}>
              Continue with email instead
            </Link>
          </div>

          <Button className="mt-14" size="lg" onClick={handleSendOtp}>
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
